import { Stack, Typography, FormHelperText, Button, CircularProgress } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useHistory } from 'react-router-dom';

import { PublicRoutes } from '@/config/routes';
import { useAuth } from '@/context/AuthProvider';
import { supabase } from '@/utils/supabaseClient';

import FormPassword from '@/components/form/FormPassword';

import AuthPageWrapper from './AuthPageWrapper';

interface FormData {
  password: string;
  confirmPassword: string;
}

const ResetPasswordPage: React.FC = () => {
  const { user } = useAuth();
  const history = useHistory();
  const [formError, setFormError] = useState<Error | null>(null);
  const [isPasswordReset, setIsPasswordReset] = useState(false);

  const {
    control,
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    getValues,
  } = useForm<FormData>();

  // Redirect to login if user is not authenticated
  useEffect(() => {
    if (!user) {
      history.push(PublicRoutes.login);
    }
  }, [user, history]);

  const onSubmit = async (data: FormData) => {
    const { password } = data;
    setFormError(null);

    try {
      const { error } = await supabase.auth.updateUser({
        password,
      });

      if (error) throw error;

      setIsPasswordReset(true);

      // Redirect to login after a short delay
      setTimeout(() => {
        history.push(PublicRoutes.login);
      }, 3000);
    } catch (error) {
      setFormError(error);
    }
  };

  if (isPasswordReset) {
    return (
      <AuthPageWrapper>
        <Stack spacing={3} width="100%" textAlign="center">
          <Typography variant="h3">Password updated</Typography>
          <Typography variant="body2">
            Your password has been successfully updated. You will be redirected to the login page
            shortly.
          </Typography>
        </Stack>
      </AuthPageWrapper>
    );
  }

  return (
    <AuthPageWrapper>
      <Stack component="form" onSubmit={handleSubmit(onSubmit)} spacing={3} width="100%">
        <Stack spacing={1.5} mb={1}>
          <Typography variant="h3">Reset your password</Typography>
          <Typography variant="body2">Enter your new password below.</Typography>
        </Stack>

        <Stack spacing={2.5}>
          <FormPassword
            label="New Password"
            placeholder="Enter your new password"
            textFieldProps={{
              autoComplete: 'new-password',
              autoFocus: true,
            }}
            error={!!errors.password}
            helperText={errors.password?.message?.toString() ?? 'Must be at least 8 characters.'}
            control={control}
            required
            {...register('password', {
              required: 'Password is required',
              minLength: {
                value: 8,
                message: 'Password must be at least 8 characters',
              },
            })}
          />

          <FormPassword
            label="Confirm New Password"
            placeholder="Confirm your new password"
            textFieldProps={{
              autoComplete: 'new-password',
            }}
            error={!!errors.confirmPassword}
            helperText={errors.confirmPassword?.message?.toString()}
            control={control}
            required
            {...register('confirmPassword', {
              required: 'Please confirm your password',
              validate: value => value === getValues('password') || 'Passwords do not match',
            })}
          />

          {!!formError && <FormHelperText error>{formError.message}</FormHelperText>}
        </Stack>

        <Button
          disabled={isSubmitting}
          fullWidth
          variant="contained"
          type="submit"
          startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : undefined}
        >
          {isSubmitting ? 'Updating...' : 'Update password'}
        </Button>
      </Stack>
    </AuthPageWrapper>
  );
};

export default ResetPasswordPage;
